/** User-triggered controls for enabling, testing, and removing this device's Push subscription. */
import React, { useEffect, useState } from 'react';

import type { PushNotifications, PushStatus } from './pushNotifications/index.js';

export interface PushNotificationSettingsProps {
  notifications: PushNotifications;
}

const problemMessage = (problem: unknown): string =>
  problem instanceof Error ? problem.message : 'Push notifications could not be updated.';

export const PushNotificationSettings = ({
  notifications,
}: PushNotificationSettingsProps): React.JSX.Element => {
  const [status, setStatus] = useState<PushStatus | 'loading'>('loading');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void notifications
      .getStatus()
      .then((nextStatus) => {
        if (active) setStatus(nextStatus);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setStatus('disabled');
        setProblem(problemMessage(error));
      });
    return () => {
      active = false;
    };
  }, [notifications]);

  const run = async (operation: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setProblem(null);
    setConfirmation(null);
    try {
      await operation();
    } catch (error) {
      setProblem(problemMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const enable = (): Promise<void> =>
    run(async () => {
      await notifications.enable();
      setStatus('enabled');
      setConfirmation('Notifications are enabled on this device.');
    });

  const disable = (): Promise<void> =>
    run(async () => {
      await notifications.disable();
      setStatus('disabled');
      setConfirmation('Notifications are disabled on this device.');
    });

  const sendTest = (): Promise<void> =>
    run(async () => {
      const result = await notifications.sendTest();
      if (result.failed > 0) throw new Error('The test notification could not be delivered.');
      setConfirmation(
        result.sent === 1
          ? 'Test notification sent to this device.'
          : `Test notification sent to ${result.sent} devices.`,
      );
    });

  return (
    <section aria-labelledby="push-notifications-heading">
      <h2 id="push-notifications-heading">Notifications</h2>
      {status === 'loading' && <p>Checking notification support…</p>}
      {status === 'unsupported' && <p>This browser does not support Web Push.</p>}
      {status === 'denied' && (
        <p>Notifications are blocked. Allow them in this site’s Android or browser settings.</p>
      )}
      {status === 'disabled' && (
        <button type="button" disabled={busy} onClick={() => void enable()}>
          Enable notifications
        </button>
      )}
      {status === 'enabled' && (
        <div className="form-actions">
          <button type="button" disabled={busy} onClick={() => void sendTest()}>
            Send test notification
          </button>
          <button
            className="secondary"
            type="button"
            disabled={busy}
            onClick={() => void disable()}
          >
            Disable notifications
          </button>
        </div>
      )}
      {problem !== null && <p role="alert">{problem}</p>}
      {confirmation !== null && <p role="status">{confirmation}</p>}
    </section>
  );
};
